import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
import { FaUserMd, FaHospital, FaAmbulance, FaStethoscope, FaHeartbeat, FaBrain, FaBone, FaBaby, FaArrowRight, FaSyringe, FaMicroscope, FaWheelchair, FaLungs, FaShareAlt, FaRegHeart, FaStar, FaMapMarkerAlt, FaCheckCircle, FaQuoteRight, FaPhoneAlt, FaPlay, FaUser, FaComments, FaMars, FaVenus } from 'react-icons/fa';
import Base from './Base';

const Counter = ({ end, duration = 1000, suffix = "" }) => {
  const [count, setCount] = React.useState(0);
  const [isVisible, setIsVisible] = React.useState(false);
  const countRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{count}{suffix}</span>;
};

// Helper: taller portrait from Unsplash so full head shows (sar na katay)
const getDoctorImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('unsplash.com')) {
    const base = url.split('?')[0];
    return `${base}?w=520&h=700&fit=crop&crop=entropy&q=85`;
  }
  return url;
};

function Home() {
  const services = [
    { icon: <FaHeartbeat />, title: "Cardiology", desc: "Our cardiology department offers comprehensive care for heart-related conditions." },
    { icon: <FaBrain />, title: "Neurology", desc: "Advanced treatment and diagnostics for disorders of the nervous system." },
    { icon: <FaBone />, title: "Orthopedics", desc: "Specialized care for musculoskeletal injuries and degenerative conditions." },
    { icon: <FaBaby />, title: "Pediatrics", desc: "Expert medical care for infants, children, and adolescents." },
  ];

  const testimonials = [
    {
      name: "Ahmed Hassan",
      role: "Patient",
      gender: "male",
      content: "The care I received at Aman Medical Complex was exceptional. The staff was professional, and the facilities were world-class.",
      rating: 5
    },
    {
      name: "Sara Khan",
      role: "Patient",
      gender: "female",
      content: "I am so grateful for the compassionate care provided by the pediatric team. They made my daughter feel comfortable and safe.",
      rating: 5
    },
    {
      name: "Manzoor Khan",
      role: "Patient",
      gender: "male",
      content: "From diagnostics to recovery, the process was seamless. The medical experts here are truly dedicated to providing the best possible treatment.",
      rating: 5
    }
  ];

  const topDoctors = [
    {
      name: "Dr. Sudhair Alam",
      specialty: "Neurologist",
      degree: "MBBS, MD, DM, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/sudhair.jpeg"
    },
    {
      name: "Dr. Iram Javed",
      specialty: "Cardiologist",
      degree: "MBBS, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/Iram.png"
    },
    {
      name: "Dr. Sajid Rauf",
      specialty: "Surgeon",
      degree: "MBBS, MD, DM, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/Sajid.jpg"
    },
    {
      name: "Dr. Shaista Ahmad",
      specialty: "Gynocologist",
      degree: "MBBS, DM, FCPS",
      address: "Aman Medical Complex, Sakwar, Gilgit",
      rating: "5.0",
      img: "/assets/images/Shaisa.png"
    }
  ];

  const [heroIndex, setHeroIndex] = useState(0);
  const [hospIndex, setHospIndex] = useState(0);
  const heroSlides = [
    {
      title: "Caring Today For A Healthier Tomorrow And Forever",
      subtitle: "We are team of talented doctors making health better",
      img: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=1600"
    },
    {
      title: "Best Medical Services For Your Health",
      subtitle: "Advanced technology and expert medical team at your service",
      img: "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=1600"
    },
    {
      title: "Your Family's Health Is Our Priority",
      subtitle: "Comprehensive healthcare for all ages and families",
      img: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=80&w=1600"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroSlides.length);
    }, 2000); // Changed to 2 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <Base>
      <div className="dark:bg-dark-800 transition-colors duration-200">
        <Helmet><title>Aman Medical Complex Gilgit - Caring for Your Health</title></Helmet>

        {/* 1. Hero Section */}
        <section className="relative min-h-[50vh] md:min-h-[70vh] lg:min-h-[90vh] flex items-start md:items-center bg-gray-50 dark:bg-dark-900 overflow-hidden -mt-[120px] md:-mt-[160px] lg:-mt-[220px] pt-[120px] md:pt-[160px] lg:pt-[220px] pb-12 md:pb-0">
          {/* Image Slider Container */}
          <div className="absolute inset-0 z-0">
            {heroSlides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  index === heroIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                <img
                  src={slide.img}
                  alt={`Hospital Banner ${index + 1}`}
                  className={`w-full h-full object-cover brightness-110 scale-105 transition-transform duration-[2000ms] ease-in-out ${
                    index === heroIndex ? 'scale-100' : 'scale-110'
                  }`}
                />
              </div>
            ))}
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-white/25 to-white/15 dark:from-dark-900/50 dark:via-dark-900/35 dark:to-dark-900/25 z-20"></div>
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 z-30 relative pb-10 md:pb-0">
            <div className="max-w-4xl">
              <div className="bg-white/85 dark:bg-dark-800/85 backdrop-blur-md rounded-2xl px-5 pt-5 pb-10 md:p-8 shadow-xl border border-white/30 dark:border-dark-700/30 transition-all duration-500">
                <div className="inline-block mb-4">
                  <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs md:text-sm flex items-center gap-2">
                    <span className="text-primary-500 text-lg">•</span>
                    WELCOME TO AMAN MEDICAL COMPLEX GILGIT
                    <span className="text-primary-500 text-lg">•</span>
                  </span>
                </div>
                <div className="overflow-hidden">
                  <h1 
                    key={`title-${heroIndex}`}
                    className="text-xl md:text-2xl lg:text-3xl font-black text-navy-500 dark:text-white mb-4 leading-tight tracking-tight animate-fadeInUp"
                  >
                    {heroSlides[heroIndex].title}
                  </h1>
                </div>
                <div className="overflow-hidden">
                  <p 
                    key={`subtitle-${heroIndex}`}
                    className="text-sm md:text-base text-gray-600 dark:text-gray-400 mb-6 md:mb-8 leading-relaxed animate-fadeIn max-w-2xl"
                  >
                    {heroSlides[heroIndex].subtitle}
                  </p>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Link to="/appointment" className="group bg-primary-500 text-white px-6 py-3 md:px-8 md:py-4 rounded-full font-bold text-sm md:text-base transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 md:gap-3 shadow-lg hover:shadow-primary-500/30 w-full sm:w-auto">
                    Make An Appointment <FaArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  {/* <Link to="/services" className="group bg-white dark:bg-dark-700 text-navy-500 dark:text-white px-8 py-4 rounded-full font-bold text-base transition-all duration-300 hover:scale-105 active:scale-95 flex items-center gap-3 shadow-soft border border-gray-200 dark:border-dark-600">
                    Our Services <FaArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link> */}
                </div>

                {/* Slider Indicators */}
                <div className="flex items-center justify-center gap-2 mt-6 mb-1">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setHeroIndex(index)}
                      className={`transition-all duration-500 rounded-full ${
                        index === heroIndex
                          ? 'w-8 h-2 bg-primary-500 shadow-lg shadow-primary-500/50'
                          : 'w-2 h-2 bg-white/50 dark:bg-gray-600 hover:bg-white/70 dark:hover:bg-gray-500'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => setHeroIndex((heroIndex - 1 + heroSlides.length) % heroSlides.length)}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 bg-white/90 dark:bg-dark-800/90 backdrop-blur-md rounded-full flex items-center justify-center text-primary-500 shadow-xl hover:bg-white dark:hover:bg-dark-700 transition-all duration-300 hover:scale-110 active:scale-95 border border-white/20 dark:border-dark-700/20"
            aria-label="Previous slide"
          >
            <FaArrowRight size={18} className="rotate-180" />
          </button>
          <button
            onClick={() => setHeroIndex((heroIndex + 1) % heroSlides.length)}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 bg-white/90 dark:bg-dark-800/90 backdrop-blur-md rounded-full flex items-center justify-center text-primary-500 shadow-xl hover:bg-white dark:hover:bg-dark-700 transition-all duration-300 hover:scale-110 active:scale-95 border border-white/20 dark:border-dark-700/20"
            aria-label="Next slide"
          >
            <FaArrowRight size={18} />
          </button>
        </section>

        {/* 2. Feature Boxes */}
        <section className="container mx-auto px-4 -mt-10 md:-mt-20 z-20 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-dark-700 p-8 shadow-card rounded-2xl border border-gray-100 dark:border-dark-600 card-hover-lift">
              <div className="w-16 h-16 bg-primary-50 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center text-primary-500 text-3xl mb-6">
                <FaAmbulance />
              </div>
              <h3 className="text-xl font-black mb-3 text-navy-500 dark:text-white">Emergency Service</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">Call us for immediate medical assistance. We are available 24/7 for emergencies.</p>
            </div>
            <div className="bg-gradient-primary p-8 shadow-card-hover rounded-2xl text-white card-hover-lift">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white text-3xl mb-6">
                <FaUserMd />
              </div>
              <h3 className="text-xl font-black mb-3">Qualified Doctors</h3>
              <h4 className="text-base font-semibold opacity-90 mb-2">Expert Team</h4>
              <p className="opacity-90 text-sm leading-relaxed">Our team consists of board-certified specialists in various medical fields.</p>
            </div>
            <div className="bg-white dark:bg-dark-700 p-8 shadow-card rounded-2xl border border-gray-100 dark:border-dark-600 card-hover-lift">
              <div className="w-16 h-16 bg-primary-50 dark:bg-primary-900/20 rounded-2xl flex items-center justify-center text-primary-500 text-3xl mb-6">
                <FaHospital />
              </div>
              <h3 className="text-xl font-black mb-3 text-navy-500 dark:text-white">Modern Facilities</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">Equipped with the latest medical technology for accurate diagnosis and treatment.</p>
            </div>
          </div>
        </section>

        {/* 3. Services Section */}
        <section className="py-24 bg-gray-50 dark:bg-dark-900/50">
          <div className="container mx-auto px-4 text-center mb-16">
            <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block">OUR SERVICES</span>
            <h2 className="text-3xl md:text-4xl font-black text-navy-500 dark:text-white mb-4 leading-tight">Our Medical Services</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              We provide wide range of medical services to ensure that our patients receive the best care possible.
            </p>
          </div>
          <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <div key={i} className="bg-white dark:bg-dark-800 p-8 rounded-2xl shadow-soft hover:shadow-card-hover transition-all group border border-gray-100 dark:border-dark-700 card-hover-lift">
                <div className="w-16 h-16 bg-primary-50 dark:bg-primary-900/20 text-primary-500 rounded-2xl flex items-center justify-center text-3xl mb-6 group-hover:bg-primary-500 group-hover:text-white transition-all icon-bounce">
                  {s.icon}
                </div>
                <h3 className="text-xl font-black text-navy-500 dark:text-white mb-4 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">{s.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm leading-relaxed">{s.desc}</p>
                <Link to="/services" className="inline-flex items-center gap-2 text-primary-500 font-bold text-sm hover:gap-3 transition-all group/link">
                  Read More <FaArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* 4. Top Rated Doctors Section */}
        <section className="py-24 container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block">OUR EXPERTS</span>
            <h2 className="text-3xl md:text-4xl font-black text-navy-500 dark:text-white">Meet The Top Rated Doctors</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {topDoctors.map((doc, i) => (
              <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl border border-gray-100 dark:border-dark-700 group transition-all duration-300 hover:-translate-y-1">
                {/* Doctor Image - full width, no blank; head visible via object-position */}
                <div className="relative w-full h-64 overflow-hidden bg-gray-100 dark:bg-dark-700 shrink-0">
                  <img
                    src={getDoctorImageUrl(doc.img)}
                    alt={doc.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    style={{ objectPosition: 'center 20%' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=520&h=700&fit=crop&q=85';
                    }}
                  />
                  <div className="absolute top-3 left-3 right-3 flex justify-between items-start opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button type="button" className="w-9 h-9 bg-white/95 dark:bg-dark-800 rounded-lg flex items-center justify-center text-primary-500 shadow-lg hover:bg-primary-500 hover:text-white transition-all transform hover:scale-110 active:scale-95">
                      <FaShareAlt size={14} />
                    </button>
                    <button type="button" className="w-9 h-9 bg-white/95 dark:bg-dark-800 rounded-lg flex items-center justify-center text-primary-500 shadow-lg hover:bg-primary-500 hover:text-white transition-all transform hover:scale-110 active:scale-95">
                      <FaRegHeart size={14} />
                    </button>
                  </div>
                </div>

                {/* Doctor Content */}
                <div className="p-4">
                  <div className="flex justify-between items-center gap-2 mb-2">
                    <span className="bg-primary-50 dark:bg-primary-900/25 text-primary-600 dark:text-primary-400 px-3 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider">{doc.specialty}</span>
                    <div className="flex items-center gap-1">
                      <FaStar className="text-amber-400 text-xs" />
                      <span className="text-xs text-gray-700 dark:text-gray-300 font-bold">{doc.rating}</span>
                    </div>
                  </div>

                  <h3 className="text-base font-black text-navy-500 dark:text-white mb-0.5 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors leading-tight">
                    {doc.name}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 font-medium text-[10px] mb-2 uppercase tracking-wider">{doc.degree}</p>

                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-[10px] font-medium mb-3">
                    <FaMapMarkerAlt className="text-primary-500 flex-shrink-0 mt-0.5" />
                    <span className="leading-snug line-clamp-2">{doc.address}</span>
                  </div>

                  <div className="flex items-center gap-2 border-t border-gray-100 dark:border-dark-700 pt-3">
                    <Link to="/appointment" className="group/btn flex-1 h-9 bg-primary-50 dark:bg-primary-900/25 text-primary-600 dark:text-primary-400 rounded-xl font-bold text-[10px] flex items-center justify-center gap-1.5 hover:bg-primary-500 hover:text-white dark:hover:bg-primary-500 dark:hover:text-white transition-all duration-300">
                      Book Now <FaArrowRight size={9} className="group-hover/btn:translate-x-0.5 transition-transform" />
                    </Link>
                    <Link to="/appointment" className="h-9 w-9 bg-primary-500 hover:bg-primary-600 text-white rounded-xl flex items-center justify-center transition-all duration-300 shadow-md active:scale-95 flex-shrink-0">
                      <FaPhoneAlt size={10} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 5. About Aman Medical Complex – Premium layout, no empty space */}
        <section className="relative py-10 md:py-14 overflow-hidden bg-primary-50 dark:bg-primary-900/20">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-100/60 via-primary-50/80 to-primary-100/60 dark:from-primary-900/30 dark:via-primary-900/20 dark:to-primary-900/30" aria-hidden="true" />
          <div className="container relative mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-10">
              {/* Left: Premium image block – mobile-friendly */}
              <div className="lg:w-[48%] flex flex-col gap-4">
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-primary-200/50 dark:border-dark-600 bg-gray-100 dark:bg-dark-700 min-h-[220px] sm:min-h-[280px] aspect-[4/3] sm:aspect-auto sm:flex-1">
                  <img
                    src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=800"
                    alt="Aman Medical Complex Gilgit – Healthcare Facility"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" aria-hidden="true" />
                  <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
                    <div className="bg-white/95 dark:bg-dark-800/95 backdrop-blur-sm rounded-xl px-3 py-2 sm:px-4 sm:py-3 border border-white/20 shadow-lg">
                      <p className="text-navy-600 dark:text-white font-bold text-xs sm:text-sm">Aman Medical Complex</p>
                      <p className="text-gray-600 dark:text-gray-300 text-[10px] sm:text-xs">Sakwar, Gilgit — Your Trusted Healthcare Partner</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="relative rounded-2xl overflow-hidden border border-primary-200/50 dark:border-dark-600 shadow-lg bg-gray-100 dark:bg-dark-700 h-[140px] sm:h-[180px] md:h-[220px]">
                    <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=600" alt="Medical care at AMC" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity" aria-hidden="true" />
                  </div>
                  <div className="relative rounded-2xl overflow-hidden border border-primary-200/50 dark:border-dark-600 shadow-lg bg-gray-100 dark:bg-dark-700 h-[140px] sm:h-[180px] md:h-[220px]">
                    <img
                      src="/assets/images/amcg.webp"
                      alt="Expert doctors AMC"
                      className="absolute inset-0 w-full h-full object-contain sm:object-cover bg-gray-200 dark:bg-dark-600"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity" aria-hidden="true" />
                  </div>
                </div>
              </div>

              {/* Right: Content – tighter spacing */}
              <div className="lg:w-[52%] flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary-500/15 dark:bg-primary-500/25 text-primary-600 dark:text-primary-400 px-3 py-1.5 mb-3 w-fit">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                  <span className="font-bold text-[10px] uppercase tracking-widest">About Aman Medical Complex</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-black text-navy-600 dark:text-white mb-3 leading-tight tracking-tight">
                  Quality Healthcare for Gilgit &amp; Beyond
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed max-w-xl text-sm">
                  Aman Medical Complex in Sakwar, Gilgit is a trusted multispecialty facility dedicated to compassionate, evidence-based care. We bring together experienced doctors, modern equipment, and a patient-first approach so families across Gilgit-Baltistan receive the best possible treatment close to home.
                </p>
                <ul className="space-y-1 mb-5 text-gray-600 dark:text-gray-300 text-xs">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-primary-500 flex-shrink-0 text-[10px]" />
                    <span>Multispecialty OPD &amp; emergency care</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-primary-500 flex-shrink-0 text-[10px]" />
                    <span>Board-certified specialists &amp; FCPS doctors</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-primary-500 flex-shrink-0 text-[10px]" />
                    <span>Modern diagnostics &amp; patient-friendly environment</span>
                  </li>
                </ul>

                {/* Stats – compact */}
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  <div className="bg-white dark:bg-dark-800 rounded-xl p-3 sm:p-4 text-center border border-primary-100 dark:border-dark-600 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-xl sm:text-2xl font-black text-primary-500 dark:text-primary-400 mb-0">15+</p>
                    <p className="text-gray-500 dark:text-gray-400 font-semibold text-[10px] uppercase tracking-wider">Years Experience</p>
                  </div>
                  <div className="bg-white dark:bg-dark-800 rounded-xl p-3 sm:p-4 text-center border border-primary-100 dark:border-dark-600 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-xl sm:text-2xl font-black text-primary-500 dark:text-primary-400 mb-0">50K+</p>
                    <p className="text-gray-500 dark:text-gray-400 font-semibold text-[10px] uppercase tracking-wider">Patients Served</p>
                  </div>
                  <div className="bg-white dark:bg-dark-800 rounded-xl p-3 sm:p-4 text-center border border-primary-100 dark:border-dark-600 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-xl sm:text-2xl font-black text-primary-500 dark:text-primary-400 mb-0">20+</p>
                    <p className="text-gray-500 dark:text-gray-400 font-semibold text-[10px] uppercase tracking-wider">Expert Doctors</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5.5. How Appointment Works Section */}
        <section className="py-24 bg-gray-50 dark:bg-dark-900/50">
          <div className="container mx-auto px-4">
            {/* Section Header */}
            <div className="text-center mb-16">
              <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                WORKING PROCESS
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-navy-500 dark:text-white mb-4 leading-tight">
                How Aman Medical Complex Gilgit Appointment Works
              </h2>
            </div>

            {/* 3 Steps Process */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Step 1 */}
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden shadow-card-hover border-4 border-white dark:border-dark-700 group-hover:scale-105 transition-transform duration-300">
                    <img
                      src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=400"
                      alt="Search Online Professional"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-white font-black text-lg shadow-xl border-4 border-white dark:border-dark-800">
                    01
                  </div>
                </div>
                <h3 className="text-xl font-black text-navy-500 dark:text-white mb-3">
                  Search Best Online Professional
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  Explore our comprehensive list of highly qualified and experienced medical professionals ready to provide exceptional care.
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden shadow-card-hover border-4 border-primary-500 group-hover:scale-105 transition-transform duration-300">
                    <img
                      src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=400"
                      alt="View Doctor Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-white font-black text-lg shadow-xl border-4 border-white dark:border-dark-800">
                    02
                  </div>
                </div>
                <h3 className="text-xl font-black text-navy-500 dark:text-white mb-3">
                  View Professional Doctor Profile
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  We are committed to delivering personalized healthcare services tailored to meet the needs of every patient at our center.
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-48 h-48 mx-auto rounded-full overflow-hidden shadow-card-hover border-4 border-white dark:border-dark-700 group-hover:scale-105 transition-transform duration-300">
                    <img
                      src="https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?auto=format&fit=crop&q=80&w=400"
                      alt="Get Schedule Appointment"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-white font-black text-lg shadow-xl border-4 border-white dark:border-dark-800">
                    03
                  </div>
                </div>
                <h3 className="text-xl font-black text-navy-500 dark:text-white mb-3">
                  Get Your Schedule Appointment
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  Visit our medical center to discuss your health concerns and receive preventive care and routine check-ups.
                </p>
              </div>
            </div>

            {/* Video Tours */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Tour Video 01 */}
              <div className="relative rounded-[40px] overflow-hidden shadow-strong group h-80">
                <img
                  src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=800"
                  alt="Hospital Tour 01"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                {/* Play Button */}
                <button className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-primary-500 shadow-2xl hover:scale-110 transition-transform group-hover:bg-primary-500 group-hover:text-white">
                  <FaPlay className="ml-1" size={24} />
                </button>

                {/* Tour Badge */}
                <div className="absolute bottom-6 left-6 bg-primary-500 text-white px-5 py-2 rounded-full font-bold text-xs uppercase tracking-wider shadow-xl">
                  TOUR VIDEO 01
                </div>
              </div>

              {/* Tour Video 02 */}
              <div className="relative rounded-[40px] overflow-hidden shadow-strong group h-80">
                <img
                  src="https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=800"
                  alt="Hospital Tour 02"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/20 to-transparent"></div>

                {/* Play Button */}
                <button className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-primary-500 shadow-2xl hover:scale-110 transition-transform group-hover:bg-primary-500 group-hover:text-white">
                  <FaPlay className="ml-1" size={24} />
                </button>

                {/* Tour Badge */}
                <div className="absolute bottom-6 left-6 bg-primary-500 text-white px-5 py-2 rounded-full font-bold text-xs uppercase tracking-wider shadow-xl">
                  TOUR VIDEO 02
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5.6. Featured Hospitals Carousel */}
        <section className="py-24 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                  FEATURED HOSPITALS
                  <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                </span>
                <h2 className="text-3xl md:text-4xl font-black text-navy-500 dark:text-white">Featured Hospitals</h2>
              </div>
              <div className="hidden md:flex gap-4">
                <button onClick={() => setHospIndex((hospIndex - 1 + 3) % 3)} className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center hover:bg-primary-600 transition-all active:scale-95 shadow-lg shadow-primary-500/20">
                  <FaArrowRight size={14} className="rotate-180" />
                </button>
                <button onClick={() => setHospIndex((hospIndex + 1) % 3)} className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center hover:bg-primary-600 transition-all active:scale-95 shadow-lg shadow-primary-500/20">
                  <FaArrowRight size={14} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { name: "City General Hospital", address: "Aman Medical Complex, Gilgit", rating: "4.8", doctors: "45+", img: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=800" },
                { name: "Aman Cardiac Center", address: "Aman Medical Complex, Gilgit", rating: "4.9", doctors: "30+", img: "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=800" },
                { name: "Aman Medical Complex", address: "Aman Medical Complex, Gilgit", rating: "4.7", doctors: "50+", img: "/assets/images/amcg.webp" }
              ].slice(hospIndex, hospIndex + 3).map((hosp, i) => (
                <div key={i} className="bg-white dark:bg-dark-800 rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover border border-gray-100 dark:border-dark-700 group card-hover-lift">
                  <div className="relative h-48 sm:h-52 md:h-56 overflow-hidden bg-gray-100 dark:bg-dark-700">
                    <img src={hosp.img} alt={hosp.name} className="w-full h-full object-contain sm:object-cover object-center group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-5 right-5 flex gap-2">
                      <button className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-primary-500 shadow-xl hover:bg-primary-500 hover:text-white transition-all">
                        <FaShareAlt size={16} />
                      </button>
                      <button className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-primary-500 shadow-xl hover:bg-primary-500 hover:text-white transition-all">
                        <FaRegHeart size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-1.5">
                        <FaStar className="text-orange-400 text-sm" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-bold">{hosp.rating}</span>
                      </div>
                      <span className="bg-primary-50 dark:bg-primary-900/20 text-primary-500 px-3 py-1 rounded-lg font-bold text-xs">{hosp.doctors} Doctors</span>
                    </div>
                    <h3 className="text-lg font-black text-navy-500 dark:text-white mb-2 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">{hosp.name}</h3>
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium mb-4">
                      <FaMapMarkerAlt className="text-primary-500" />
                      {hosp.address}
                    </div>
                    <Link to="/hospitals" className="inline-flex items-center gap-2 text-primary-500 font-bold text-sm hover:gap-3 transition-all group/link">
                      View Details <FaArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Why Choose Section */}

        <section className="py-24 bg-gradient-to-br from-navy-500 to-navy-700 dark:from-dark-900 dark:to-dark-800 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="lg:w-1/2">
                <span className="text-primary-400 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block">WHY CHOOSE US</span>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-6 leading-tight">Why Choose Our Medical Services?</h2>
                <p className="text-white/80 text-lg mb-10 leading-relaxed">
                  Aman Medical Complex Gilgit provides state-of-the-art facilities and a compassionate approach to patient care, led by a team of highly-qualified medical specialists.
                </p>
                <ul className="space-y-5">
                  {[
                    "Advanced Diagnostic Technology",
                    "Board-Certified Medical Specialists",
                    "24/7 Emergency & Critical Care",
                    "Personalized Treatment Plans"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-4 text-white text-lg font-semibold">
                      <span className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm flex-shrink-0">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:w-1/2 grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">
                    <Counter end={25} suffix="k+" />
                  </h4>
                  <p className="text-white/80 font-semibold">Happy Patients</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 mt-10">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">
                    <Counter end={150} suffix="+" />
                  </h4>
                  <p className="text-white/80 font-semibold">Specialist Doctors</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 -mt-10">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">
                    <Counter end={24} suffix="/7" />
                  </h4>
                  <p className="text-white/80 font-semibold">High Priority Care</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20">
                  <h4 className="text-5xl font-black text-primary-400 mb-2">
                    <Counter end={12} suffix="+" />
                  </h4>
                  <p className="text-white/80 font-semibold">Departments</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7. Testimonials Section */}
        <section className="py-24 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block">PATIENT TESTIMONIALS</span>
              <h2 className="text-3xl md:text-4xl font-black text-navy-500 dark:text-white leading-tight">
                What Our Patients Say
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testi, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-dark-800 p-8 rounded-3xl shadow-soft hover:shadow-card-hover transition-all group relative overflow-hidden flex flex-col h-full border border-gray-100 dark:border-dark-700 card-hover-lift"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-primary-50 dark:bg-primary-900/10 rounded-bl-full translate-x-10 -translate-y-10 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/20 transition-colors"></div>

                  <div className="flex gap-1 text-orange-400 mb-6">
                    {[...Array(testi.rating)].map((_, i) => <FaStar key={i} size={14} />)}
                  </div>

                  <p className="text-gray-600 dark:text-gray-300 text-sm font-medium italic leading-relaxed mb-8 flex-grow relative z-10">
                    "{testi.content}"
                  </p>

                  <div className="flex items-center gap-4 relative z-10 mt-auto">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-md border-2 border-primary-100 dark:border-primary-900/30 bg-primary-50 dark:bg-primary-900/20 text-primary-500 dark:text-primary-400">
                      {testi.gender === "male" ? <FaMars size={22} /> : <FaVenus size={22} />}
                    </div>
                    <div>
                      <h4 className="text-base font-black text-navy-500 dark:text-white mb-0.5">{testi.name}</h4>
                      <p className="text-primary-500 font-semibold uppercase tracking-wider text-xs">{testi.role}</p>
                    </div>
                    <div className="ml-auto text-primary-200 dark:text-primary-900/30 group-hover:text-primary-300 dark:group-hover:text-primary-900/40 transition-colors">
                      <FaQuoteRight size={24} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* 7.5. Latest News & Articles Section */}
        <section className="py-24 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-4">
            {/* Section Header */}
            <div className="text-center mb-16">
              <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block flex items-center justify-center gap-2">
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                LATEST NEWS
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-navy-500 dark:text-white mb-4 leading-tight">
                Read Our Latest Insights & Articles
              </h2>
            </div>

            {/* Articles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Article 1 */}
              <div className="bg-white dark:bg-dark-800 rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover border border-gray-100 dark:border-dark-700 group card-hover-lift">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=600"
                    alt="Healthcare Benefits"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-primary-500 text-white px-4 py-2 rounded-xl font-black text-xs shadow-xl">
                    28 Jan
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400 text-xs font-medium mb-4">
                    <span className="flex items-center gap-2">
                      <FaUser className="text-primary-500" size={12} /> Admin
                    </span>
                    <span className="flex items-center gap-2">
                      <FaComments className="text-primary-500" size={12} /> 02
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-navy-500 dark:text-white mb-3 leading-tight group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                    Benefiting From Healthcare Shouldn't Be A Matter Of Luck
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 leading-relaxed">
                    We are committed to delivering exceptional healthcare services tailored to meet the needs of every patient.
                  </p>
                  <Link to="/blog" className="inline-flex items-center gap-2 text-primary-500 font-bold text-sm hover:gap-3 transition-all group/link">
                    Read More <FaArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Article 2 */}
              <div className="bg-white dark:bg-dark-800 rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover border border-gray-100 dark:border-dark-700 group card-hover-lift">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&q=80&w=600"
                    alt="Patient Care"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-primary-500 text-white px-4 py-2 rounded-xl font-black text-xs shadow-xl">
                    22 Jan
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400 text-xs font-medium mb-4">
                    <span className="flex items-center gap-2">
                      <FaUser className="text-primary-500" size={12} /> Admin
                    </span>
                    <span className="flex items-center gap-2">
                      <FaComments className="text-primary-500" size={12} /> 03
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-navy-500 dark:text-white mb-3 leading-tight group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                    Caring For The Whole Patient Not Just Your Symptoms
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 leading-relaxed">
                    Our medical center provides comprehensive care that looks at the person as a whole, not just the ailment.
                  </p>
                  <Link to="/blog" className="inline-flex items-center gap-2 text-primary-500 font-bold text-sm hover:gap-3 transition-all group/link">
                    Read More <FaArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Article 3 */}
              <div className="bg-white dark:bg-dark-800 rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover border border-gray-100 dark:border-dark-700 group card-hover-lift">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1551076805-e1869033e561?auto=format&fit=crop&q=80&w=600"
                    alt="Advanced Technology"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-primary-500 text-white px-4 py-2 rounded-xl font-black text-xs shadow-xl">
                    18 Jan
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400 text-xs font-medium mb-4">
                    <span className="flex items-center gap-2">
                      <FaUser className="text-primary-500" size={12} /> Admin
                    </span>
                    <span className="flex items-center gap-2">
                      <FaComments className="text-primary-500" size={12} /> 05
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-navy-500 dark:text-white mb-3 leading-tight group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                    Advanced Technology in Modern Surgical Procedures
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 leading-relaxed">
                    Exploring how the latest technological advancements are making surgeries safer and more efficient.
                  </p>
                  <Link to="/blog" className="inline-flex items-center gap-2 text-primary-500 font-bold text-sm hover:gap-3 transition-all group/link">
                    Read More <FaArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Article 4 */}
              <div className="bg-white dark:bg-dark-800 rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover border border-gray-100 dark:border-dark-700 group card-hover-lift">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&q=80&w=600"
                    alt="Preventive Care"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-primary-500 text-white px-4 py-2 rounded-xl font-black text-xs shadow-xl">
                    15 Jan
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400 text-xs font-medium mb-4">
                    <span className="flex items-center gap-2">
                      <FaUser className="text-primary-500" size={12} /> Admin
                    </span>
                    <span className="flex items-center gap-2">
                      <FaComments className="text-primary-500" size={12} /> 01
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-navy-500 dark:text-white mb-3 leading-tight group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                    The Importance of Preventive Care and Regular Checkups
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 leading-relaxed">
                    Preventive medicine is the best way to ensure long-term health and catch potential issues early.
                  </p>
                  <Link to="/blog" className="inline-flex items-center gap-2 text-primary-500 font-bold text-sm hover:gap-3 transition-all group/link">
                    Read More <FaArrowRight size={12} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Map Section - Visit Our Medical Complex */}
        <section className="py-24 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Left Side - Info Card */}
              <div className="bg-gradient-to-br from-navy-500 to-navy-700 dark:from-dark-800 dark:to-dark-900 rounded-[45px] p-12 text-white shadow-strong">
                <div className="inline-block bg-primary-500 text-white px-6 py-2 rounded-full font-bold text-xs uppercase tracking-wider mb-6">
                  BEST RATED LOCATION
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-6 leading-tight">
                  Visit Our Medical Complex
                </h2>
                <p className="text-white/80 text-base mb-10 leading-relaxed">
                  Experience world-class healthcare in the heart of Gilgit. Our facility is easily accessible and equipped with modern amenities.
                </p>

                {/* Address */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-primary-400 text-xl flex-shrink-0">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <h3 className="text-lg font-black mb-1">Our Address</h3>
                    <p className="text-white/70 text-sm">Aman Medical Complex, Sakwar, Gilgit, Pakistan</p>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="flex items-start gap-4 mb-10">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-primary-400 text-xl flex-shrink-0">
                    <FaPhoneAlt />
                  </div>
                  <div>
                    <h3 className="text-lg font-black mb-1">Emergency Contact</h3>
                    <p className="text-white/70 text-sm">+92-345 5558297</p>
                  </div>
                </div>

                {/* Get Directions Button */}
                <a
                  href="https://www.google.com/maps/place/Aman+Medical+Complex+Sakwar/@35.9196,74.3089,17z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-full font-black text-base transition-all duration-300 hover:scale-105 active:scale-95 shadow-xl w-full"
                >
                  Get Directions <FaArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Right Side - Google Map */}
              <div className="rounded-[45px] overflow-hidden shadow-strong h-[500px] border-4 border-white dark:border-dark-800">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3283.123456789!2d74.3089!3d35.9196!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzXCsDU1JzEwLjYiTiA3NMKwMTgnMzIuMCJF!5e0!3m2!1sen!2s!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Aman Medical Complex Location"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* 9. CTA Section */}
        <section className="py-20 container mx-auto px-4">
          <div className="bg-gradient-to-r from-primary-500 to-primary-700 rounded-3xl p-12 md:p-16 flex flex-col lg:flex-row items-center justify-between gap-10 relative overflow-hidden shadow-strong">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="lg:w-2/3 text-white text-center lg:text-left relative z-10">
              <h2 className="text-3xl md:text-4xl font-black mb-6 leading-tight">Need a Doctor for Checkup?</h2>
              <p className="text-lg opacity-90 mb-6 font-medium">Please book your appointment through our online system or give us a call.</p>
              <div className="flex flex-col sm:flex-row items-center gap-4 text-xl font-black">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-lg shadow-lg">
                  <FaPhoneAlt />
                </div>
                <span className="text-white">+92-345 5558297</span>
              </div>
            </div>
            <div className="lg:w-1/3 text-center relative z-10">
              <Link to="/appointment" className="group inline-flex items-center justify-center gap-3 bg-white text-primary-600 hover:bg-gray-50 px-8 py-4 rounded-full font-black text-lg transition-all duration-300 hover:scale-105 active:scale-95 shadow-2xl">
                Get Appointment Now <FaArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Base>
  );
}

export default Home;
